import { supabase } from '@/integrations/supabase/client';

export interface RealtimeMessage {
  type: string;
  [key: string]: any;
}

export interface RealtimeTool {
  type: 'function';
  name: string;
  description: string;
  parameters: Record<string, any>;
}

export interface RealtimeChatOptions {
  onMessage: (message: RealtimeMessage) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onError?: (error: Error) => void;
  onSpeakingChange?: (isSpeaking: boolean) => void;
  onTranscript?: (text: string, isFinal: boolean) => void;
  /** Live partial transcript of what the AI is saying (assistant captions). */
  onAiTranscript?: (text: string, isFinal: boolean) => void;
  /** Live partial transcript of what the user is saying. */
  onUserTranscriptDelta?: (delta: string) => void;
  /** The model wants to call a tool. Resolve via sendFunctionResult(callId, output). */
  onFunctionCall?: (name: string, args: any, callId: string) => void;
  /** The remote (AI voice) MediaStream — use it to drive an audio-reactive visualizer. */
  onRemoteStream?: (stream: MediaStream) => void;
}

export class RealtimeChat {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private audioEl: HTMLAudioElement;
  private localStream: MediaStream | null = null;
  private options: RealtimeChatOptions;
  private isConnected = false;

  constructor(options: RealtimeChatOptions) {
    this.options = options;
    this.audioEl = document.createElement("audio");
    this.audioEl.autoplay = true;
  }

  getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  async connect(instructions?: string, voice: string = 'cedar', tools?: RealtimeTool[]) {
    try {
      console.log('🎤 Requesting microphone access...');
      
      // Request microphone access first
      this.localStream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });

      console.log('🔑 Getting ephemeral token from edge function...');
      
      // Get ephemeral token from our edge function
      const { data, error } = await supabase.functions.invoke('openai-realtime-session', {
        body: { instructions, voice, tools }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(`Failed to get session token: ${error.message}`);
      }

      if (!data?.client_secret?.value) {
        console.error('Invalid response from edge function:', data);
        throw new Error("Failed to get ephemeral token - no client_secret in response");
      }

      const EPHEMERAL_KEY = data.client_secret.value;
      console.log('✅ Got ephemeral token');

      // Create peer connection
      this.pc = new RTCPeerConnection();

      // Set up remote audio playback
      this.pc.ontrack = (e) => {
        console.log('🔊 Received audio track from OpenAI');
        this.audioEl.srcObject = e.streams[0];
        this.options.onRemoteStream?.(e.streams[0]);
      };

      // Add local audio track
      const audioTrack = this.localStream.getTracks()[0];
      this.pc.addTrack(audioTrack);
      console.log('🎙️ Added local audio track');

      // Set up data channel for events
      this.dc = this.pc.createDataChannel("oai-events");
      
      this.dc.addEventListener("open", () => {
        console.log('📡 Data channel opened');
        this.isConnected = true;
        this.options.onConnected?.();
      });

      this.dc.addEventListener("close", () => {
        console.log('📡 Data channel closed');
        this.isConnected = false;
        this.options.onDisconnected?.();
      });

      this.dc.addEventListener("message", (e) => {
        try {
          const event = JSON.parse(e.data);
          this.handleEvent(event);
        } catch (err) {
          console.error('Failed to parse event:', err);
        }
      });

      // Create and set local description
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      console.log('📤 Created WebRTC offer');

      // Connect to OpenAI's GA Realtime API. The ephemeral token already carries the
      // session config (model, voice, tools), so no query params are needed.
      const sdpResponse = await fetch("https://api.openai.com/v1/realtime/calls", {
        method: "POST",
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${EPHEMERAL_KEY}`,
          "Content-Type": "application/sdp"
        },
      });

      if (!sdpResponse.ok) {
        const errorText = await sdpResponse.text();
        throw new Error(`WebRTC connection failed: ${sdpResponse.status} - ${errorText}`);
      }

      const answerSdp = await sdpResponse.text();
      const answer: RTCSessionDescriptionInit = {
        type: "answer",
        sdp: answerSdp,
      };
      
      await this.pc.setRemoteDescription(answer);
      console.log('✅ WebRTC connection established');

    } catch (error) {
      console.error("Error initializing realtime chat:", error);
      this.options.onError?.(error instanceof Error ? error : new Error(String(error)));
      this.disconnect();
      throw error;
    }
  }

  private handleEvent(event: RealtimeMessage) {
    console.log('📨 Received event:', event.type);
    
    // Forward all events to callback
    this.options.onMessage(event);

    switch (event.type) {
      case 'session.created':
        console.log('✅ Session created');
        break;

      case 'session.updated':
        console.log('✅ Session updated');
        break;

      case 'input_audio_buffer.speech_started':
        console.log('🎤 User started speaking');
        break;

      case 'input_audio_buffer.speech_stopped':
        console.log('🎤 User stopped speaking');
        break;

      case 'conversation.item.input_audio_transcription.delta':
        // User's speech transcribed incrementally (live captions)
        if (event.delta) this.options.onUserTranscriptDelta?.(event.delta);
        break;

      case 'conversation.item.input_audio_transcription.completed':
        // User's speech transcribed
        const userTranscript = event.transcript;
        if (userTranscript) {
          console.log('📝 User transcript:', userTranscript);
          this.options.onTranscript?.(userTranscript, true);
        }
        break;

      case 'response.audio_transcript.delta':
        // AI is generating text (partial) — live caption of what Plutus is saying
        if (event.delta) this.options.onAiTranscript?.(event.delta, false);
        break;

      case 'response.audio_transcript.done':
        // AI finished generating text for this response
        const aiTranscript = event.transcript;
        if (aiTranscript) {
          console.log('🤖 AI transcript:', aiTranscript);
          this.options.onAiTranscript?.(aiTranscript, true);
        }
        break;

      case 'response.function_call_arguments.done':
        // The model wants to call one of our tools.
        try {
          const args = event.arguments ? JSON.parse(event.arguments) : {};
          console.log('🛠️ Function call:', event.name, args);
          this.options.onFunctionCall?.(event.name, args, event.call_id);
        } catch (err) {
          console.error('Failed to parse function args:', err);
        }
        break;

      case 'response.audio.delta':
        // AI is speaking (audio chunk)
        this.options.onSpeakingChange?.(true);
        break;

      case 'response.audio.done':
        // AI finished speaking
        console.log('🔊 AI finished speaking');
        this.options.onSpeakingChange?.(false);
        break;

      case 'response.done':
        console.log('✅ Response complete');
        this.options.onSpeakingChange?.(false);
        break;

      case 'error':
        console.error('❌ OpenAI error:', event.error);
        this.options.onError?.(new Error(event.error?.message || 'Unknown OpenAI error'));
        break;
    }
  }

  sendTextMessage(text: string) {
    if (!this.dc || this.dc.readyState !== 'open') {
      console.error('Data channel not ready');
      return;
    }

    console.log('📤 Sending text message:', text);

    // Create conversation item
    const createEvent = {
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [
          {
            type: 'input_text',
            text
          }
        ]
      }
    };

    this.dc.send(JSON.stringify(createEvent));
    
    // Trigger response generation
    this.dc.send(JSON.stringify({ type: 'response.create' }));
  }

  updateSession(config: any) {
    if (!this.dc || this.dc.readyState !== 'open') {
      console.error('Data channel not ready for session update');
      return;
    }

    console.log('📤 Updating session config');
    this.dc.send(JSON.stringify({
      type: 'session.update',
      session: config
    }));
  }

  /**
   * Share an image (e.g. the user's current chart) with the model so it can SEE it.
   * Pass triggerResponse=true to make Plutus comment on it immediately.
   */
  sendImage(imageUrl: string, text?: string, triggerResponse = false) {
    if (!this.dc || this.dc.readyState !== 'open') {
      console.error('Data channel not ready for image');
      return;
    }
    const content: any[] = [{ type: 'input_image', image_url: imageUrl }];
    if (text) content.push({ type: 'input_text', text });
    this.dc.send(JSON.stringify({
      type: 'conversation.item.create',
      item: { type: 'message', role: 'user', content },
    }));
    if (triggerResponse) this.dc.send(JSON.stringify({ type: 'response.create' }));
    console.log('🖼️ Sent chart image to Plutus', triggerResponse ? '(with response)' : '(context only)');
  }

  /** Return the result of a tool call back to the model, then let it continue speaking. */
  sendFunctionResult(callId: string, output: unknown) {
    if (!this.dc || this.dc.readyState !== 'open') {
      console.error('Data channel not ready for function result');
      return;
    }
    this.dc.send(JSON.stringify({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: typeof output === 'string' ? output : JSON.stringify(output),
      },
    }));
    this.dc.send(JSON.stringify({ type: 'response.create' }));
  }

  disconnect() {
    console.log('🔌 Disconnecting...');
    
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }
    
    if (this.dc) {
      this.dc.close();
      this.dc = null;
    }
    
    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }

    this.audioEl.srcObject = null;
    this.isConnected = false;
    
    console.log('✅ Disconnected');
  }

  get connected() {
    return this.isConnected;
  }
}
